import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Play, Pause, Check, Trash2,
  Users, Send, CheckCircle2, XCircle, BookOpen,
  MessageCircle, Mail, Smartphone, Loader2,
  CalendarClock, Tag, Filter, TrendingUp,
  AlertCircle, Eye, Radio,
} from 'lucide-react';
import { useCampaign, useAudiencePreview } from '../hooks/useCampaigns';
import { useUpdateCampaign, useUpdateCampaignStatus, useDeleteCampaign } from '../hooks/useCampaignMutations';
import { useSendCampaign, useCampaignMessageStats, useCampaignMessages } from '../hooks/useSendCampaign';
import { CampaignForm } from './CampaignForm';
import { CampaignStatusBadge, CampaignTypeBadge } from './CampaignStatusBadge';
import { CampaignDispatchModal } from './CampaignDispatchModal';
import type { CampaignInsert } from '../types';
import { CAMPAIGN_TYPE_LABELS } from '../types';

/* ─── helpers ────────────────────────────────────────────────── */
function fmtDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(iso));
}
function fmtDate(iso: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso));
}

/* ─── Card de métrica ────────────────────────────────────────── */
function MetricCard({ label, value, sub, icon: Icon, color = 'text-ink-muted' }: {
  label: string; value: number | string; sub?: string;
  icon: React.ElementType; color?: string;
}) {
  return (
    <div className="rounded-2xl card-premium p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">{label}</span>
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <span className="font-display text-3xl font-extrabold text-ink tabular-nums">
        {typeof value === 'number' ? value.toLocaleString('pt-BR') : value}
      </span>
      {sub && <span className="text-[11px] text-ink-subtle">{sub}</span>}
    </div>
  );
}

/* ─── Seção ──────────────────────────────────────────────────── */
function Section({ icon: Icon, title, children }: {
  icon: React.ElementType; title: string; children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl card-premium p-5 space-y-4">
      <div className="flex items-center gap-2 hairline-b pb-3">
        <Icon className="h-4 w-4 text-accent" />
        <h3 className="font-display text-sm font-bold text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ─── Página ─────────────────────────────────────────────────── */
export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen,        setEditOpen]        = useState(false);
  const [dispatchOpen,    setDispatchOpen]    = useState(false);
  const [activeTab,       setActiveTab]       = useState<'overview' | 'messages'>('overview');
  const [sendError,       setSendError]       = useState<string | null>(null);

  const { data: campaign, isLoading } = useCampaign(id ?? null);
  const updateMutation  = useUpdateCampaign();
  const statusMutation  = useUpdateCampaignStatus();
  const deleteMutation  = useDeleteCampaign();
  const sendMutation    = useSendCampaign();

  // Audiência estimada (para o modal de confirmação); audienceFilter é declarado abaixo após o guard
  const { data: audienceCount = 0 } = useAudiencePreview(campaign?.audience_filter ?? {});

  const isRunning = campaign?.status === 'running';
  const { data: msgStats } = useCampaignMessageStats(id, isRunning);
  const { data: messages } = useCampaignMessages(activeTab === 'messages' ? id : undefined);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }
  if (!campaign) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <XCircle className="h-12 w-12 text-danger/60" />
        <p className="text-ink-muted">Campanha não encontrada.</p>
        <button onClick={() => navigate('/app/campanhas')} className="btn-ghost px-4 py-2 rounded-xl text-sm">
          Voltar
        </button>
      </div>
    );
  }

  const handleEdit = (data: CampaignInsert) => {
    updateMutation.mutate({ id: campaign.id, payload: data }, { onSuccess: () => setEditOpen(false) });
  };

  const handleDelete = () => {
    if (!confirm(`Remover "${campaign.name}" permanentemente?`)) return;
    deleteMutation.mutate(campaign.id, { onSuccess: () => navigate('/app/campanhas') });
  };

  const handleSend = () => {
    setSendError(null);
    setDispatchOpen(true);
  };

  const handleConfirmDispatch = (testMode: boolean) => {
    setDispatchOpen(false);
    // Dispara em background e navega para o War Room imediatamente.
    // Se falhar, o status da campanha permanece 'draft' e o
    // War Room exibe o banner de timeout (15 s) levando o usuário de volta.
    sendMutation.mutate({ campaignId: campaign!.id, testMode }, {
      onError: (e) => setSendError(e instanceof Error ? e.message : 'Erro ao disparar campanha'),
    });
    navigate(`/app/campanhas/${campaign!.id}/war-room`);
  };

  const deliveryRate = campaign.stats_sent > 0
    ? Math.round((campaign.stats_delivered / campaign.stats_sent) * 100)
    : 0;

  const TypeIcon = campaign.type === 'whatsapp' ? MessageCircle
    : campaign.type === 'email' ? Mail : Smartphone;

  const isDraft     = campaign.status === 'draft';
  const isScheduled = campaign.status === 'scheduled';
  const isPaused    = campaign.status === 'paused';

  const audienceFilter = campaign.audience_filter;

  // Progresso de envio (quando running)
  const sendProgress = msgStats && msgStats.total > 0
    ? Math.round(((msgStats.total - msgStats.queued) / msgStats.total) * 100)
    : 0;

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6 pb-16 animate-fade-in">

        {/* ── Topbar ── */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => navigate('/app/campanhas')}
            className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Campanhas</span>
          </button>
          <span className="text-ink-subtle">/</span>
          <span className="text-sm font-semibold text-ink truncate max-w-[200px]">{campaign.name}</span>

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* Ações de status */}
            {isDraft && (
              <>
                <button
                  onClick={handleSend}
                  disabled={!campaign.message_body}
                  className="flex items-center gap-1.5 rounded-xl btn-accent px-3 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  <Send className="h-3.5 w-3.5" />
                  <span>Disparar agora</span>
                </button>
                <button
                  onClick={() => setEditOpen(true)}
                  className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-semibold"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Editar</span>
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                  className="rounded-xl p-2 btn-ghost text-ink-muted hover:text-danger transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
            {isScheduled && (
              <button
                onClick={() => statusMutation.mutate({ id: campaign.id, status: 'draft' })}
                className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-semibold"
              >
                <XCircle className="h-3.5 w-3.5" />
                Cancelar agendamento
              </button>
            )}
            {isRunning && (
              <>
                <button
                  onClick={() => navigate(`/app/campanhas/${campaign.id}/war-room`)}
                  className="flex items-center gap-1.5 rounded-xl btn-accent px-3 py-2 text-sm font-semibold"
                >
                  <Radio className="h-3.5 w-3.5" />
                  <span>War Room</span>
                </button>
                <button
                  onClick={() => statusMutation.mutate({ id: campaign.id, status: 'paused' })}
                  className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-semibold"
                >
                  <Pause className="h-3.5 w-3.5" />
                  Pausar
                </button>
              </>
            )}
            {isPaused && (
              <>
                <button
                  onClick={() => navigate(`/app/campanhas/${campaign.id}/war-room`)}
                  className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-semibold"
                >
                  <Radio className="h-3.5 w-3.5" />
                  War Room
                </button>
                <button
                  onClick={() => statusMutation.mutate({ id: campaign.id, status: 'running' })}
                  className="flex items-center gap-1.5 rounded-xl btn-accent px-3 py-2 text-sm font-semibold"
                >
                  <Play className="h-3.5 w-3.5" />
                  Retomar
                </button>
                <button
                  onClick={() => statusMutation.mutate({ id: campaign.id, status: 'completed' })}
                  className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-semibold"
                >
                  <Check className="h-3.5 w-3.5" />
                  Encerrar
                </button>
              </>
            )}
          </div>
        </div>

        {/* ── Erro de disparo ── */}
        {sendError && (
          <div className="flex items-start gap-3 rounded-xl bg-danger/10 border border-danger/20 px-4 py-3">
            <AlertCircle className="h-4 w-4 text-danger shrink-0 mt-0.5" />
            <p className="text-sm text-danger flex-1">{sendError}</p>
            <button onClick={() => setSendError(null)} className="text-danger/60 hover:text-danger transition-colors">
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* ── Barra de progresso de envio ── */}
        {isRunning && msgStats && msgStats.total > 0 && (
          <div className="rounded-2xl card-premium p-5 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-accent" />
                <span className="font-semibold text-ink">Enviando mensagens…</span>
              </div>
              <span className="font-mono text-xs text-ink-muted">
                {msgStats.total - msgStats.queued} / {msgStats.total}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-midnight-4 overflow-hidden">
              <div
                className="h-2 rounded-full bg-accent transition-all duration-500"
                style={{ width: `${sendProgress}%` }}
              />
            </div>
            <div className="flex items-center gap-4 text-[11px] text-ink-subtle flex-wrap">
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                {msgStats.delivered} entregues
              </span>
              <span className="flex items-center gap-1">
                <BookOpen className="h-3 w-3 text-accent" />
                {msgStats.read} lidos
              </span>
              {msgStats.failed > 0 && (
                <span className="flex items-center gap-1 text-danger">
                  <XCircle className="h-3 w-3" />
                  {msgStats.failed} falhas
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── Hero ── */}
        <div className="relative overflow-hidden rounded-3xl glass-strong shadow-deep p-6 md:p-8">
          <div
            className="absolute -top-20 -right-20 h-56 w-56 rounded-full blur-3xl pointer-events-none opacity-20"
            style={{ background: 'color-mix(in oklab, var(--color-accent), transparent 50%)' }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-2xl blur-xl bg-accent/15" />
              <div className="relative h-16 w-16 rounded-2xl bg-midnight-3 hairline flex items-center justify-center">
                <TypeIcon className="h-7 w-7 text-accent" />
              </div>
            </div>
            <div className="flex-grow min-w-0 space-y-2">
              <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-ink">
                {campaign.name}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                <CampaignStatusBadge status={campaign.status} />
                <CampaignTypeBadge type={campaign.type} />
              </div>
              {campaign.description && (
                <p className="text-sm text-ink-muted">{campaign.description}</p>
              )}
              {campaign.status === 'failed' && campaign.failure_reason && (
                <div className="flex items-start gap-2 rounded-lg bg-danger/10 border border-danger/30 px-3 py-2 text-sm text-danger">
                  <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{campaign.failure_reason}</span>
                </div>
              )}
            </div>
            {campaign.stats_targeted > 0 && (
              <div className="shrink-0 text-right">
                <p className="font-display text-3xl font-extrabold text-ink">
                  {deliveryRate}%
                </p>
                <p className="text-xs text-ink-muted">taxa de entrega</p>
              </div>
            )}
          </div>
        </div>

        {/* ── Métricas ── */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard
            label="Público-alvo"
            value={campaign.stats_targeted > 0 ? campaign.stats_targeted : audienceCount}
            icon={Users}
            color="text-blue-300"
            sub={campaign.stats_targeted === 0 && audienceCount > 0 ? 'estimativa' : undefined}
          />
          <MetricCard label="Enviados"       value={campaign.stats_sent}      icon={Send}         color="text-ink-muted" />
          <MetricCard label="Entregues"      value={campaign.stats_delivered} icon={CheckCircle2} color="text-emerald-400" />
          <MetricCard label="Lidos"          value={campaign.stats_read}      icon={BookOpen}     color="text-accent" />
          <MetricCard label="Falhas"         value={campaign.stats_failed}    icon={XCircle}      color="text-danger" />
        </div>

        {/* ── Tabs ── */}
        <div className="flex items-center gap-1 rounded-xl bg-midnight-2 hairline p-1 w-fit">
          {(['overview', 'messages'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${
                activeTab === tab
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:text-ink'
              }`}
            >
              {tab === 'overview'
                ? <><TrendingUp className="h-3.5 w-3.5" /> Visão Geral</>
                : <><Eye className="h-3.5 w-3.5" /> Mensagens {msgStats?.total ? `(${msgStats.total})` : ''}</>
              }
            </button>
          ))}
        </div>

        {/* ── Tab: Mensagens ── */}
        {activeTab === 'messages' && (
          <MessagesTab messages={messages ?? []} />
        )}

        {/* ── Layout duas colunas ── */}
        {activeTab === 'overview' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Coluna principal */}
          <div className="lg:col-span-2 space-y-5">

            {/* Mensagem */}
            <Section icon={MessageCircle} title="Mensagem">
              {campaign.message_body ? (
                <div className="rounded-xl bg-midnight-2/60 hairline p-4">
                  <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
                    {campaign.message_body}
                  </p>
                  <div className="flex items-center justify-between mt-3 pt-3 hairline-t">
                    <span className="font-mono text-[10px] text-ink-subtle">
                      {campaign.message_body.length} caracteres · {CAMPAIGN_TYPE_LABELS[campaign.type]}
                    </span>
                    {isDraft && (
                      <button
                        onClick={() => setEditOpen(true)}
                        className="text-xs font-semibold text-accent hover:underline"
                      >
                        Editar
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 space-y-2">
                  <p className="text-sm text-ink-faint">Nenhuma mensagem definida ainda.</p>
                  {isDraft && (
                    <button onClick={() => setEditOpen(true)} className="text-xs font-semibold text-accent hover:underline">
                      + Adicionar mensagem
                    </button>
                  )}
                </div>
              )}
            </Section>

            {/* Progresso de entrega */}
            {campaign.stats_targeted > 0 && (
              <Section icon={TrendingUp} title="Progresso de entrega">
                <div className="space-y-3">
                  {[
                    { label: 'Enviados',   value: campaign.stats_sent,      total: campaign.stats_targeted, color: 'bg-ink-subtle' },
                    { label: 'Entregues',  value: campaign.stats_delivered, total: campaign.stats_targeted, color: 'bg-emerald-400' },
                    { label: 'Lidos',      value: campaign.stats_read,      total: campaign.stats_targeted, color: 'bg-accent' },
                    { label: 'Falhas',     value: campaign.stats_failed,    total: campaign.stats_targeted, color: 'bg-danger' },
                  ].map(({ label, value, total, color }) => {
                    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                    return (
                      <div key={label} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-ink-muted">{label}</span>
                          <span className="font-mono text-ink-subtle">
                            {value.toLocaleString('pt-BR')} <span className="text-ink-faint">({pct}%)</span>
                          </span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-midnight-4 overflow-hidden">
                          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Section>
            )}
          </div>

          {/* Coluna lateral */}
          <div className="space-y-5">

            {/* Audiência */}
            <Section icon={Filter} title="Público-alvo">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between py-1">
                  <span className="text-ink-subtle">Apenas ativos</span>
                  <span className="text-ink font-medium">
                    {audienceFilter?.activeOnly !== false ? 'Sim' : 'Não'}
                  </span>
                </div>
                {audienceFilter?.categories?.length ? (
                  <div className="py-1">
                    <p className="text-ink-subtle mb-2">Categorias</p>
                    <div className="flex flex-wrap gap-1.5">
                      {audienceFilter.categories.map((c) => (
                        <span key={c} className="rounded-full border border-hairline px-2 py-0.5 text-[11px] text-ink-muted">
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-ink-subtle">Categorias</span>
                    <span className="text-ink-faint text-xs">Todas</span>
                  </div>
                )}
                {audienceFilter?.tags?.length ? (
                  <div className="py-1">
                    <p className="text-ink-subtle mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1.5">
                      {audienceFilter.tags.map((t) => (
                        <span key={t} className="rounded-full border border-hairline px-2 py-0.5 text-[11px] text-ink-muted">
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-ink-subtle">Tags</span>
                    <span className="text-ink-faint text-xs">Todas</span>
                  </div>
                )}
                {audienceFilter?.neighborhood && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-ink-subtle">Bairro</span>
                    <span className="text-ink">{audienceFilter.neighborhood}</span>
                  </div>
                )}
              </div>
            </Section>

            {/* Agendamento */}
            <Section icon={CalendarClock} title="Agendamento">
              <div className="space-y-1 divide-y divide-hairline">
                {[
                  { label: 'Criada em',    value: fmtDate(campaign.created_at) },
                  { label: 'Agendada para', value: fmtDateTime(campaign.scheduled_at) },
                  { label: 'Iniciada em',   value: fmtDateTime(campaign.started_at) },
                  { label: 'Concluída em',  value: fmtDateTime(campaign.completed_at) },
                ].map(({ label, value }) => value !== '—' && (
                  <div key={label} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-ink-subtle">{label}</span>
                    <span className="text-ink font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Base legal */}
            <Section icon={Tag} title="Conformidade LGPD">
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-ink-subtle">Base legal</span>
                  <span className="text-ink font-medium capitalize">
                    {campaign.legal_basis === 'legitimate_interest' ? 'Legítimo interesse'
                      : campaign.legal_basis === 'consent' ? 'Consentimento'
                      : 'Contrato'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ink-subtle">Canal</span>
                  <span className="text-ink">{CAMPAIGN_TYPE_LABELS[campaign.type]}</span>
                </div>
              </div>
            </Section>
          </div>
        </div>}  {/* end activeTab === 'overview' */}
      </div>

      {/* Modal de edição — usa createPortal internamente */}
      {editOpen && (
        <CampaignForm
          campaign={campaign}
          onSubmit={handleEdit}
          onClose={() => setEditOpen(false)}
          isPending={updateMutation.isPending}
        />
      )}

      {/* Modal de confirmação de disparo */}
      {dispatchOpen && (
        <CampaignDispatchModal
          campaign={campaign}
          audienceCount={audienceCount}
          onConfirm={handleConfirmDispatch}
          onClose={() => setDispatchOpen(false)}
        />
      )}
    </>
  );
}

// ── Tab: lista de mensagens individuais ───────────────────────────────────────

type MsgRow = {
  id: string;
  phone: string;
  status: string;
  error_code: string | null;
  error_title: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  read_at: string | null;
  failed_at: string | null;
  contact: { id: string; full_name: string } | null;
};

const MSG_STATUS_COLORS: Record<string, string> = {
  queued:    'bg-slate-500/15 text-slate-300',
  sent:      'bg-blue-500/15 text-blue-300',
  delivered: 'bg-emerald-500/15 text-emerald-300',
  read:      'bg-accent/15 text-accent',
  failed:    'bg-danger/15 text-danger',
};
const MSG_STATUS_LABELS: Record<string, string> = {
  queued:    'Na fila',
  sent:      'Enviado',
  delivered: 'Entregue',
  read:      'Lido',
  failed:    'Falha',
};

function MessagesTab({ messages }: { messages: MsgRow[] }) {
  if (messages.length === 0) {
    return (
      <div className="rounded-2xl card-premium p-12 flex flex-col items-center gap-3 text-center">
        <MessageCircle className="h-10 w-10 text-ink-subtle opacity-30" />
        <p className="text-sm text-ink-muted">Nenhuma mensagem disparada ainda.</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl card-premium overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="hairline-b">
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Contato</th>
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Telefone</th>
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Status</th>
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Enviado em</th>
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Entregue em</th>
              <th className="px-4 py-3 text-left font-mono text-[10px] uppercase tracking-widest text-ink-subtle">Lido em</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {messages.map((msg) => (
              <tr key={msg.id} className="hover:bg-white/[0.02] transition-colors">
                <td className="px-4 py-3 text-ink font-medium">
                  {msg.contact?.full_name ?? <span className="text-ink-faint">—</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-ink-muted">{msg.phone}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${MSG_STATUS_COLORS[msg.status] ?? ''}`}>
                    {MSG_STATUS_LABELS[msg.status] ?? msg.status}
                  </span>
                  {msg.status === 'failed' && msg.error_title && (
                    <p className="text-[10px] text-danger mt-0.5">{msg.error_title}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-ink-subtle">{fmtTime(msg.sent_at)}</td>
                <td className="px-4 py-3 text-xs text-ink-subtle">{fmtTime(msg.delivered_at)}</td>
                <td className="px-4 py-3 text-xs text-ink-subtle">{fmtTime(msg.read_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {messages.length === 200 && (
        <p className="px-4 py-3 text-xs text-ink-faint hairline-t">
          Exibindo os primeiros 200 registros.
        </p>
      )}
    </div>
  );
}

function fmtTime(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}
